import * as z from 'zod';

// ============================================
// Service Price API Payload types
// ============================================

const serviceAreaSchema = z.object({
    zipCode: z.string(),
    countryCode: z.string(),
    stateCode: z.string().optional(),
});

const servicePriceItemLineSchema = z.object({
    itemType: z.string(),
    itemNo: z.string(),
    id: z.union([z.string(), z.number()]),
    requiredQty: z.number(),
    unitOfMeasure: z.string(),
});

const itemLineReferenceSchema = z.object({
    lineId: z.union([z.string(), z.number()]),
    quantity: z.number(),
});

const timeSlotSchema = z.object({
    id: z.string(),
    date: z.string(),
    timeFrom: z.string(),
    timeTo: z.string(),
});

const deliveryServiceSchema = z.object({
    deliveryMethod: z.string(),
    deliveryMethodId: z.string(),
    deliveryType: z.string(),
    id: z.string(),
    itemLineReferences: z.array(itemLineReferenceSchema),
    serviceArea: serviceAreaSchema,
    timeSlots: z.array(timeSlotSchema),
    storageFromDate: z.string(),
    solutionReference: z.string().optional(),
});

const customerContactSchema = z.object({
    addressLine1: z.string().optional(),
    addressLine2: z.string().optional(),
    addressLine3: z.string().optional(),
    businessName: z.string().optional(),
    city: z.string().optional(),
    countryCode: z.string().optional(),
    email: z.string().optional(),
    firstName: z.string().optional(),
    id: z.string().optional(),
    ikeaFamilyNumber: z.string().optional(),
    isBusinessCustomer: z.boolean().optional(),
    landlinePhone: z.string().optional(),
    lastName: z.string().optional(),
    mobilePhone: z.string().optional(),
    partyId: z.string().optional(),
    preferredLocale: z.string().optional(),
    stateCode: z.string().optional(),
    vatPayer: z.string().optional(),
    zipCode: z.string().optional(),
});

const customerSchema = z.object({
    deliveryAddress: customerContactSchema.optional(),
    billingAddress: customerContactSchema.optional(),
    isFamilyCustomer: z.boolean(),
    isEmployee: z.boolean(),
});

export const servicePricePayloadSchema = z.object({
    buCode: z.string(),
    serviceRequest: z.object({
        serviceArea: serviceAreaSchema,
        itemLines: z.array(servicePriceItemLineSchema),
        deliveryServices: z.array(deliveryServiceSchema),
        pickupPoints: z.array(z.unknown()).optional(),
        solutions: z.array(z.string()).optional(),
        customer: customerSchema.optional(),
    }),
});

export type ServicePricePayload = z.infer<typeof servicePricePayloadSchema>;

// ============================================
// Service Price API Response types
// ============================================

const servicePriceReferenceSchema = z.object({
    lineId: z.string().optional(),
});

export const servicePriceEntryPriceSchema = z.object({
    priceInclTax: z.number().optional(),
    priceExclTax: z.number().optional(),
    amount: z.number().optional(),
    currencyCode: z.string().optional(),
    taxDetails: z.array(z.unknown()).optional(),
});

export type ServicePriceEntryPrice = z.infer<typeof servicePriceEntryPriceSchema>;

export type PriceValueField = keyof Pick<ServicePriceEntryPrice, 'priceExclTax' | 'priceInclTax'>;

const servicePriceResponseItemSchema = z.object({
    deliveryServiceReference: servicePriceReferenceSchema.optional(),
    solutionReference: servicePriceReferenceSchema.optional(),
    price: servicePriceEntryPriceSchema.optional(),
});

export const servicePriceResponseSchema = z.object({
    deliveryServicePrices: z.array(servicePriceResponseItemSchema).optional(),
    solutionPrices: z.array(servicePriceResponseItemSchema).optional(),
    providedServicePrices: z.array(servicePriceResponseItemSchema).optional(),
});

export type ServicePriceResponse = z.infer<typeof servicePriceResponseSchema>;

export const parseServicePriceApiResponse = (data: unknown): ServicePriceResponse => {
    const result = servicePriceResponseSchema.safeParse(data);
    return result.success ? result.data : {};
};
